import { Link } from 'react-router-dom';
import { articles, categories, faqItems, stats, siteInfo } from '@/mocks/siteData';
import FAQAccordion from '@/components/feature/FAQAccordion';

export default function Home() {
  const latestArticles = articles.slice(0, 6);
  const featuredArticle = articles[0];

  return (
    <>
      {/* Hero Section */}
      <section className="relative w-full h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20digital%20landscape%20with%20warm%20terracotta%20amber%20and%20deep%20brown%20tones%2C%20ethereal%20light%20rays%20piercing%20through%20geometric%20shapes%2C%20representing%20internet%20culture%20and%20digital%20phenomenon%2C%20minimalist%20composition%2C%20thought-provoking%20editorial%20style%2C%20high%20contrast%2C%20soft%20gradients%20with%20dramatic%20lighting&width=1800&height=900&seq=hero-fuliji-home&orientation=landscape"
            alt="福利姬完全指南 - 深入解析网络文化现象"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
        <div className="relative z-10 w-full px-4 md:px-6 text-center max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white mb-4 md:mb-6 leading-tight">
            深入了解福利姬网络文化现象
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed">
            以客观、全面的视角解析福利姬的起源、发展、内容生态与平台规则，
            帮助你建立对这一网络文化现象的理性认知。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <Link
              to="/category/origin-development"
              className="px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              探索起源与发展
            </Link>
            <Link
              to="/category/industry-news"
              className="px-6 py-3 bg-white/15 backdrop-blur-sm text-white border border-white/30 rounded-full text-sm font-medium hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看最新动态
            </Link>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <span className="w-8 h-8 flex items-center justify-center text-white/60">
            <i className="ri-arrow-down-line text-2xl"></i>
          </span>
        </div>
      </section>

      {/* Stats Section */}
      <section className="w-full py-12 md:py-16 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl md:text-4xl font-bold font-heading text-primary-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-foreground-500">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-4">
                关于本站
              </span>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-5 leading-tight">
                什么是福利姬完全指南？
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  福利姬完全指南是一个专注于解析"福利姬"这一网络文化现象的资讯平台。我们以学术性和客观性为宗旨，通过深度文章、行业分析和资讯报道，帮助读者全面了解福利姬的定义、起源、内容生态、平台规则及相关文化背景。
                </p>
                <p>
                  福利姬作为中国互联网亚文化的重要切片，折射出当代网络内容经济、社交媒体生态和社会观念的诸多面向。我们的目标是帮助读者建立对这一现象的理性认知，而非简单地鼓励或谴责。
                </p>
                <div className="flex flex-wrap gap-3 pt-2">
                  {siteInfo.values.map((v, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-50 border border-background-200/70 rounded-full text-xs text-foreground-600"
                    >
                      <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                        <i className="ri-check-line text-sm"></i>
                      </span>
                      {v}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="w-full aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20conceptual%20art%20representing%20knowledge%20and%20analysis%2C%20geometric%20shapes%20resembling%20books%20and%20digital%20screens%2C%20warm%20terracotta%20and%20golden%20amber%20tones%2C%20minimalist%20editorial%20style%2C%20clean%20composition%2C%20professional%20aesthetic&width=800&height=600&seq=about-intro-image&orientation=landscape"
                  alt="福利姬完全指南 - 专业分析平台"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article */}
      <section className="w-full py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 md:gap-10 items-center">
            <div className="w-full md:w-1/2">
              <div className="w-full aspect-[16/10] rounded-xl overflow-hidden">
                <img
                  src={featuredArticle.image}
                  alt={featuredArticle.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="w-full md:w-1/2">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-3">
                推荐阅读
              </span>
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-3 leading-snug">
                <Link
                  to={`/article/${featuredArticle.slug}`}
                  className="hover:text-primary-600 transition-colors"
                >
                  {featuredArticle.title}
                </Link>
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                {featuredArticle.excerpt}
              </p>
              <div className="flex items-center gap-4 text-xs text-foreground-400 mb-5">
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line text-sm"></i>
                  </span>
                  {featuredArticle.date}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-sm"></i>
                  </span>
                  {featuredArticle.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-price-tag-3-line text-sm"></i>
                  </span>
                  {featuredArticle.categoryName}
                </span>
              </div>
              <Link
                to={`/article/${featuredArticle.slug}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-foreground-900 text-background-50 rounded-full text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                阅读全文
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-8 md:mb-10">
            <div>
              <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-2">
                最新内容
              </span>
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                深度解析文章
              </h2>
            </div>
            <Link
              to="/category/origin-development"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              查看全部
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </span>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {latestArticles.map((article) => (
              <article key={article.slug} className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 transition-colors hover:border-background-300/70">
                <Link to={`/article/${article.slug}`} className="block">
                  <div className="w-full aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </Link>
                <div className="p-5">
                  <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                    <span>{article.categoryName}</span>
                    <span>·</span>
                    <span>{article.date}</span>
                  </div>
                  <h3 className="text-base font-bold font-heading text-foreground-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors">
                    <Link to={`/article/${article.slug}`}>
                      {article.title}
                    </Link>
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-time-line text-xs"></i>
                      </span>
                      {article.readTime}
                    </span>
                    <Link
                      to={`/article/${article.slug}`}
                      className="text-xs text-primary-600 font-medium hover:text-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
                    >
                      阅读
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line"></i>
                      </span>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="w-full py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">
              内容分类
            </span>
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
              多维度探索福利姬文化
            </h2>
            <p className="text-sm text-foreground-500 max-w-xl mx-auto">
              从起源发展到行业动态，从平台规则到文化背景，多角度全面解析
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group bg-background-100 rounded-xl p-6 border border-background-200/70 transition-colors hover:border-primary-300/60 hover:bg-background-50 cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-100 text-primary-600 rounded-xl flex-shrink-0 group-hover:bg-primary-500 group-hover:text-white transition-colors">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-bold font-heading text-foreground-900 mb-1.5">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-3">
                      {cat.description}
                    </p>
                    <span className="text-xs text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap">
                      {cat.count} 篇文章
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line"></i>
                      </span>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full mb-3">
              常见问题
            </span>
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-3">
              关于福利姬的常见疑问
            </h2>
            <p className="text-sm text-foreground-500">
              我们整理了读者最关心的问题，帮助你快速了解福利姬现象
            </p>
          </div>
          <FAQAccordion items={faqItems} />
        </div>
      </section>

      {/* CTA / Newsletter Section */}
      <section className="w-full py-14 md:py-20 bg-background-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20pattern%20with%20warm%20terracotta%20and%20dark%20brown%20tones%2C%20subtle%20texture%2C%20minimalist%20design%2C%20clean%20repeating%20shapes%2C%20dark%20background%20with%20golden%20accents&width=1600&height=600&seq=cta-bg-texture&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 w-full px-4 md:px-6 max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-background-50 mb-3">
            订阅最新资讯
          </h2>
          <p className="text-sm text-background-300 mb-8 leading-relaxed">
            订阅我们的资讯邮件，第一时间获取福利姬文化研究的最新文章和行业动态。
          </p>
          <form
            data-readdy-form
            action="https://readdy.ai/api/form/d9qq3gue3oq4jqumtoj0"
            method="POST"
            className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              name="email"
              required
              placeholder="请输入您的邮箱地址"
              className="flex-1 px-4 py-3 rounded-full text-sm bg-background-800 border border-background-700 text-background-50 placeholder:text-background-500 focus:outline-none focus:border-primary-500 transition-colors"
            />
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="honeypot-field"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              立即订阅
            </button>
          </form>
          <p className="text-xs text-background-500 mt-4">
            我们尊重您的隐私，随时可以取消订阅
          </p>
        </div>
      </section>

      {/* Schema.org Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': '福利姬完全指南 - 首页',
        'description': '深入解析福利姬网络文化现象的起源、发展与现状，全面了解福利姬的定义、内容生态、平台规则及相关文化背景。',
        'url': 'https://yikecongkuaika.cn/',
        'isPartOf': {
          '@type': 'WebSite',
          'name': '福利姬完全指南',
          'url': 'https://yikecongkuaika.cn/',
        },
        'mainEntity': {
          '@type': 'FAQPage',
          'mainEntity': faqItems.map((item) => ({
            '@type': 'Question',
            'name': item.question,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': item.answer,
            },
          })),
        },
        'about': {
          '@type': 'Organization',
          'name': siteInfo.name,
          'description': siteInfo.description,
          'foundingDate': siteInfo.founded,
          'email': siteInfo.contact.email,
        },
      }) }}></script>
    </>
  );
}